import React, { FunctionComponent, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { Grid, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyOriginCountryDialog } from "../ModifyOriginCountryDialog";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { useContextMenu } from "../../../../hooks/contextMenu";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { OriginCountryCard } from "../OriginCountryCard";
import { IOriginCountry, useGetOriginCountries } from "../../../../hooks/originCountries";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";

export const OriginCountriesDesktop: FunctionComponent<IOriginCountriesDesktopProps> = ({
  handleClose,
  isPublic,
  columnWidth,
  preselectedId,
  onOriginCountrySelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [originCountries, resetOriginCountries, setOriginCountries, noMoreOriginCountries] =
    useGetOriginCountries(isPublic, setLoading);
  const {
    state: modifyDialogState,
    key: modifyDialogKey,
    openAdd,
    openEdit,
    close: closeModifyDialog
  } = useResettableDialog<IOriginCountry>();
  const [searchText, setSearchText] = useState("");
  const [contextOriginCountry, setContextOriginCountry] = useState<IOriginCountry>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const filteredOriginCountries = useMemo(() => {
    if (searchText == "") {
      return originCountries;
    }
    return originCountries?.filter(originCountry => {
      return (
        originCountry.name.toLowerCase().includes(searchText.toLowerCase()) ||
        originCountry.country?.isoCode?.toLowerCase().includes(searchText.toLowerCase()) ||
        originCountry.country?.isoCode2?.toLowerCase().includes(searchText.toLowerCase()) ||
        originCountry.country?.emoji?.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [originCountries, searchText]);
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={isPublic}
      items={filteredOriginCountries}
      emptyIconType={attributeIconMap[AttributeType.ORIGIN_COUNTRIES]}
      emptyString={
        isPublic ? strings.empty_origin_countries_public : strings.empty_origin_countries
      }
      addItemString={strings.add_attribute}
      title={strings.origin_countries_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={openAdd}
      refreshAction={resetOriginCountries}
      onClearValue={onClearValue}
    >
      {filteredOriginCountries?.map(originCountry => (
        <Grid item key={originCountry.uuid} xs={columnWidth}>
          <OriginCountryCard
            name={originCountry.name}
            isoCode={originCountry.country?.isoCode}
            emoji={originCountry.country?.emoji}
            onClick={() => {
              if (onOriginCountrySelected) {
                onOriginCountrySelected(originCountry);
              } else {
                openEdit(originCountry);
              }
            }}
            onContextMenu={event => {
              setContextOriginCountry(originCountry);
              handleContextMenu(event);
            }}
            selected={preselectedId == originCountry.uuid}
          />
        </Grid>
      ))}
      {(loading || !noMoreOriginCountries) &&
        Array.from({ length: 20 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 60 }} className={styles.skeleton} />
          </Grid>
        ))}
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextOriginCountry);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.alert_modify}
        </MenuItem>
      </Menu>
      <ModifyOriginCountryDialog
        key={modifyDialogKey}
        open={modifyDialogState.mode !== "closed"}
        onClose={closeModifyDialog}
        originCountry={modifyDialogState.item}
        onNewItem={newOriginCountry => {
          const newOriginCountries = [...originCountries, newOriginCountry];
          newOriginCountries.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setOriginCountries(newOriginCountries);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={newOriginCountry => {
          const newOriginCountries = originCountries.map(originCountry =>
            originCountry.uuid == newOriginCountry.uuid ? newOriginCountry : originCountry
          );
          newOriginCountries.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setOriginCountries(newOriginCountries);
          toast.success(strings.generic_update_success);
        }}
        onDeletedItem={deletedItem => {
          const newOriginCountries = originCountries.filter(
            originCountry => originCountry.uuid != deletedItem.uuid
          );
          setOriginCountries(newOriginCountries);
          toast.success(strings.generic_update_success);
        }}
      />
    </AttributeDesktop>
  );
};

export interface IOriginCountriesDesktopProps {
  handleClose?: () => void;
  isPublic: boolean;
  columnWidth: number;
  preselectedId?: string;
  onOriginCountrySelected?: (originCountry: IOriginCountry) => void;
  onClearValue?: () => void;
}
